import { UserCertificate } from '@app/store/models';
import {
  CertificateList,
  CertificateListItem,
} from '@app/store/models/certificate.model';
import moment from 'moment';

/** YYYY-MM-DD — compatibile con formatDate / orderTable in multicert. */
export function formatIssuanceDateForMultiCertTable(
  dateValue?: string | null
): string {
  if (!dateValue?.trim()) {
    return '';
  }
  const normalized = dateValue.includes('T')
    ? dateValue.substring(0, 10)
    : dateValue.trim();
  const parsed = moment(normalized, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

function mapCertificateStatusForMultiCertList(
  certificate: UserCertificate
): CertificateList['status'] {
  const status = certificate.certificateStatus?.toUpperCase();

  if (status === 'REVOKED' || status === 'SUSPENDED') {
    return 'revoked';
  }
  if (status === 'EXPIRED' || status === 'INVALID' || status === 'NOT_VALID') {
    return 'expired';
  }
  if (status === 'REGISTERED' || (!certificate.active && status === 'EMITTED')) {
    return 'to_activate';
  }

  if (certificate.validityTo) {
    const expiration = moment(certificate.validityTo.substring(0, 10));
    const daysDiff = expiration.diff(moment().startOf('day'), 'days');
    if (daysDiff <= 89 && daysDiff >= 0) {
      return 'expiring';
    }
  }

  return certificate.active ? 'active' : 'to_activate';
}

/** Mappa il dettaglio certificato al formato riga tabella multicert. */
export function mapUserCertificateToCertificateList(
  certificate: UserCertificate,
  item?: CertificateListItem
): CertificateList {
  const subjectDN = certificate.certificateInfo?.subjectDN;
  const subjectDnMap = certificate.certificateInfo?.subjectDnMap;
  const identity =
    item?.uida || (item as { uid?: string })?.uid || certificate.certId;

  const subjectName =
    subjectDN?.CN?.trim() ||
    subjectDnMap?.commonName?.trim() ||
    [subjectDN?.G, subjectDN?.SN].filter(Boolean).join(' ').trim() ||
    identity;

  const cf =
    subjectDN?.SERIALNUMBER?.trim() ||
    subjectDnMap?.serialNumber?.trim() ||
    '';

  return {
    subjectName,
    cf,
    CF: cf,
    profileName:
      certificate.certificateType?.trim() ||
      certificate.domain?.trim() ||
      certificate.aliasCertificate?.trim() ||
      '',
    dnq: certificate.aliasCertificate?.trim() || identity,
    issuanceDate: formatIssuanceDateForMultiCertTable(certificate.validityFrom),
    notAfter: certificate.validityTo?.trim() || '',
    uap: certificate.certId,
    status: mapCertificateStatusForMultiCertList(certificate),
  };
}

/** Fallback minimo se il GET dettaglio certificato fallisce. */
export function mapCertificateListItemFallback(
  item: CertificateListItem
): CertificateList {
  const identity = item.uida || (item as { uid?: string }).uid || item.certId;

  return {
    subjectName: identity,
    cf: '',
    CF: '',
    profileName: '',
    dnq: identity,
    issuanceDate: '',
    notAfter: '',
    uap: item.certId,
    status: 'active',
  };
}

/** @deprecated Usare mapUserCertificateToCertificateList dopo GET /certificates/{id} */
export function mapCertificateListItemsToCertificateList(
  items: CertificateListItem[]
): CertificateList[] {
  return (items ?? []).map(mapCertificateListItemFallback);
}

const PLACEHOLDER_SUBJECT_CN = /^ND(\s+ND)?$/i;

function hasSubjectData(certificate: UserCertificate): boolean {
  const subjectDN = certificate.certificateInfo?.subjectDN;
  const subjectDnMap = certificate.certificateInfo?.subjectDnMap;

  if (subjectDnMap) {
    const hasMapValue = Object.values(subjectDnMap).some(
      value => value != null && String(value).trim() !== ''
    );
    if (hasMapValue) {
      return true;
    }
  }

  if (subjectDN) {
    return [subjectDN.CN, subjectDN.SN, subjectDN.G, subjectDN.O].some(
      value => value != null && String(value).trim() !== ''
    );
  }

  return false;
}

function hasRealSubjectData(certificate: UserCertificate): boolean {
  if (!hasSubjectData(certificate)) {
    return false;
  }

  const subjectDN = certificate.certificateInfo?.subjectDN;
  const cn = subjectDN?.CN?.trim() ?? '';
  const sn = subjectDN?.SN?.trim() ?? '';
  const givenName = subjectDN?.G?.trim() ?? '';

  if (PLACEHOLDER_SUBJECT_CN.test(cn) && !sn && !givenName) {
    return false;
  }

  return true;
}

export interface CertificateActivationContext {
  needsForcePassword?: boolean;
  needActivationCertIds?: string[];
}

export function getCertificateActivationContext(storage: {
  retrieve(key: string): unknown;
}): CertificateActivationContext {
  const session = storage.retrieve('session') as
    | { needsForcePassword?: boolean }
    | undefined;

  return {
    needsForcePassword: session?.needsForcePassword,
    needActivationCertIds:
      (storage.retrieve('needActivation') as string[] | undefined) ?? [],
  };
}

/** Utente nel flusso attivazione (REGISTERED, cambio password, needActivation). */
export function isInActivationFlow(
  certificate: UserCertificate | undefined | null,
  context?: CertificateActivationContext
): boolean {
  if (context?.needsForcePassword) {
    return true;
  }

  if (!certificate?.certId) {
    return false;
  }

  if (certificate.certificateStatus === 'REGISTERED') {
    return true;
  }

  const needActivation = context?.needActivationCertIds ?? [];
  if (
    needActivation.includes(certificate.certId) &&
    certificate.certificateStatus !== 'EMITTED'
  ) {
    return true;
  }

  return false;
}

/** Certificato con certId ma senza dati minimi per uso operativo (cert bianco). */
export function isOperationalEmptyCertificate(
  certificate: UserCertificate | undefined | null
): boolean {
  if (!certificate?.certId) {
    return true;
  }

  if (!certificate.certificateStatus) {
    return true;
  }

  if (!certificate.validityTo?.trim()) {
    return true;
  }

  if (!hasRealSubjectData(certificate)) {
    return true;
  }

  return false;
}

/**
 * Logout su cert bianco solo fuori attivazione.
 * Usare in oauth2 prima di homepage/multi-cert e sulla pagina /cert.
 */
export function shouldLogoutForEmptyCertificate(
  certificate: UserCertificate | undefined | null,
  context?: CertificateActivationContext
): boolean {
  if (isInActivationFlow(certificate, context)) {
    return false;
  }

  return isOperationalEmptyCertificate(certificate);
}
